import { Injectable } from '@angular/core';
import Swal from 'sweetalert2';
import { MINIMUM_LOAD_AMOUNT } from '../../core/constants/constants';

@Injectable({
  providedIn: 'root',
})
export class AlertService {
  constructor() {}

  showInfoAlert(title: string, text: string, confirmButtonText = 'Cerrar') {
    return Swal.fire({
      title,
      text,
      icon: 'info',
      iconColor: '#54BA4A',
      confirmButtonText,
      confirmButtonColor: '#54BA4A',
    });
  }

  showHtmlAlert(title: string, html: string, confirmButtonText = 'Cerrar') {
    return Swal.fire({
      title,
      html,
      icon: 'info',
      iconColor: '#54BA4A',
      confirmButtonText,
      confirmButtonColor: '#54BA4A',
    });
  }

  showSuccessAlert(title: string, text: string, confirmButtonText = 'Ok') {
    return Swal.fire({
      title,
      text,
      icon: 'success',
      confirmButtonText,
      confirmButtonColor: '#54BA4A',
    });
  }

  showErrorAlert(title: string, text: string, confirmButtonText = 'Cerrar') {
    return Swal.fire({
      title,
      text,
      icon: 'error',
      confirmButtonText,
      confirmButtonColor: '#54BA4A',
      timer: 3000,
      timerProgressBar: true,
      showConfirmButton: false,
    });
  }

  showConfirmAlert(
    title: string,
    text: string,
    confirmButtonText = 'Aprobar',
    cancelButtonText = 'Rechazar'
  ) {
    return Swal.fire({
      title,
      text,
      icon: 'question',
      iconColor: '#54BA4A',
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      confirmButtonColor: '#54BA4A',
      cancelButtonColor: '#5A6268',
      reverseButtons: true,
    });
  }

  sentConfirmAlert(
    title: string,
    text: string,
    confirmButtonText = 'Enviar',
    cancelButtonText = 'No enviar'
  ) {
    return Swal.fire({
      title,
      text,
      icon: 'question',
      iconColor: '#54BA4A',
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      confirmButtonColor: '#54BA4A',
      cancelButtonColor: '#5A6268',
      reverseButtons: true,
    });
  }

  showInputAlert(
    title: string,
    inputLabel: string,
    placeholder = 'Ingrese la razón del rechazo aquí...',
    confirmButtonText = 'Rechazar',
    cancelButtonText = 'Cancelar',
    inputValidator?: (value: string) => string | null
  ) {
    return Swal.fire({
      title,
      input: 'textarea',
      inputLabel,
      inputPlaceholder: placeholder,
      inputAttributes: {
        'aria-label': inputLabel,
      },
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      confirmButtonColor: '#54BA4A',
      cancelButtonColor: '#5A6268',
      inputValidator,
    });
  }

  showTextInputAlert(
    title: string,
    inputLabel: string,
    placeholder = 'Ingrese el texto aquí...',
    confirmButtonText = 'Aceptar',
    cancelButtonText = 'Cancelar'
  ) {
    return Swal.fire({
      title,
      input: 'text',
      inputLabel,
      inputPlaceholder: placeholder,
      inputAttributes: {
        'aria-label': inputLabel,
      },
      showCancelButton: true,
      confirmButtonText,
      cancelButtonText,
      confirmButtonColor: '#54BA4A',
      cancelButtonColor: '#5A6268',
    });
  }

  showDebtConversionAlert(
    amount: number,
    minimumAmount: number = MINIMUM_LOAD_AMOUNT
  ) {
    return Swal.fire({
      icon: 'info',
      iconColor: '#17a2b8',
      title: 'Solicitud Procesada',
      html: `
        <div style="text-align: center;">
          <p style="font-size: 16px; color: #6c757d; margin-bottom: 15px;">
            El monto de <strong style="color: #007bff;">$${amount.toFixed(
              2
            )}</strong> es menor al mínimo requerido para cargas.
          </p>
          <div style="background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
                      color: white;
                      padding: 15px 20px;
                      border-radius: 10px;
                      margin: 15px 0;">
            <i class="fas fa-exchange-alt" style="font-size: 24px; margin-bottom: 8px;"></i>
            <p style="margin: 0; font-size: 14px;">Tu solicitud ha sido registrada como</p>
            <p style="margin: 5px 0 0 0; font-size: 18px; font-weight: bold;">Abono a Deuda</p>
          </div>
          <p style="font-size: 13px; color: #6c757d; margin-top: 10px;">
            <i class="fas fa-info-circle"></i> El monto mínimo para cargas es de <strong>$${minimumAmount}</strong>
          </p>
        </div>
      `,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#17a2b8',
    });
  }

  showTransactionSuccessAlert(config: {
    type: 'load' | 'withdrawal';
    amount: number;
    previousUsed: number;
    limit: number;
  }) {
    const { type, amount, previousUsed, limit } = config;
    const newUsed = previousUsed + amount;
    const previousPercentage = limit > 0 ? (previousUsed / limit) * 100 : 0;
    const newPercentage =
      limit > 0 ? Math.min((newUsed / limit) * 100, 100) : 0;
    const remaining = Math.max(0, limit - newUsed);

    const isLoad = type === 'load';
    const title = isLoad ? '¡Carga Registrada!' : '¡Retiro Registrado!';
    const transactionLabel = isLoad ? 'Carga' : 'Retiro';
    const accentColor = isLoad ? '#28a745' : '#dc3545';
    const progressBarColor = this.getProgressBarColor(newPercentage);

    // Guardar referencia para la animación
    const self = this;

    return Swal.fire({
      icon: 'success',
      iconColor: '#54BA4A',
      title,
      html: `
      <div style="text-align: center; padding: 10px 0;">
        <div style="background: ${accentColor}15;
                    border-radius: 10px;
                    padding: 15px;
                    margin-bottom: 20px;">
          <p style="margin: 0; font-size: 14px; color: #6c757d;">${transactionLabel} realizado</p>
          <p style="margin: 5px 0 0 0; font-size: 28px; font-weight: bold; color: ${accentColor};">
            +$${amount.toFixed(2)}
          </p>
        </div>

        <p style="font-size: 14px; color: #495057; margin-bottom: 8px;">
          <strong>Límite diario de ${transactionLabel.toLowerCase()}</strong>
        </p>

        <div style="display: flex; justify-content: space-between; font-size: 13px; color: #6c757d; margin-bottom: 5px;">
          <span id="swal-used-text">$${previousUsed.toFixed(2)} usado</span>
          <span>$${limit.toFixed(2)} límite</span>
        </div>

        <div style="background: #e9ecef;
                    border-radius: 10px;
                    height: 24px;
                    overflow: hidden;
                    position: relative;
                    box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);">
          <div id="swal-progress-bar"
               style="height: 100%;
                      width: ${previousPercentage}%;
                      background: linear-gradient(90deg, ${progressBarColor}, ${this.darkenColor(
        progressBarColor,
        20
      )});
                      border-radius: 10px;
                      transition: width 1.2s cubic-bezier(0.4, 0, 0.2, 1);
                      display: flex;
                      align-items: center;
                      justify-content: flex-end;
                      padding-right: 10px;
                      min-width: 45px;">
            <span id="swal-progress-text"
                  style="color: white;
                         font-size: 12px;
                         font-weight: bold;
                         text-shadow: 0 1px 2px rgba(0,0,0,0.3);">
              ${Math.round(previousPercentage)}%
            </span>
          </div>
        </div>

        <p style="font-size: 14px; color: #6c757d; margin-top: 15px;">
          <i class="fas fa-wallet" style="color: ${accentColor};"></i>
          Disponible: <strong id="swal-remaining-text" style="color: ${
            remaining > 0 ? '#28a745' : '#dc3545'
          };">$${(limit - previousUsed).toFixed(2)}</strong>
        </p>
      </div>
    `,
      showConfirmButton: false,
      timer: 5000,
      timerProgressBar: true,
      didOpen: () => {
        // Delay para que se vea la animación
        setTimeout(() => {
          self.runProgressAnimation(
            previousPercentage,
            newPercentage,
            previousUsed,
            newUsed,
            limit,
            remaining
          );
        }, 400);
      },
    });
  }
  private runProgressAnimation(
    fromPercentage: number,
    toPercentage: number,
    fromUsed: number,
    toUsed: number,
    limit: number,
    finalRemaining: number
  ): void {
    const progressBar = document.getElementById('swal-progress-bar');
    const progressText = document.getElementById('swal-progress-text');
    const usedText = document.getElementById('swal-used-text');
    const remainingText = document.getElementById('swal-remaining-text');

    if (!progressBar) {
      console.warn('Progress bar element not found');
      return;
    }

    // Animar la barra
    const newColor = this.getProgressBarColor(toPercentage);
    progressBar.style.width = `${Math.max(toPercentage, 3)}%`;
    progressBar.style.background = `linear-gradient(90deg, ${newColor}, ${this.darkenColor(
      newColor,
      20
    )})`;

    // Animar los números
    const duration = 1200;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);

      const currentPercentage =
        fromPercentage + (toPercentage - fromPercentage) * easeOut;
      const currentUsed = fromUsed + (toUsed - fromUsed) * easeOut;
      const currentRemaining = limit - currentUsed;

      if (progressText) {
        progressText.textContent = `${Math.round(currentPercentage)}%`;
      }

      if (usedText) {
        usedText.textContent = `$${currentUsed.toFixed(2)} usado`;
      }

      if (remainingText) {
        remainingText.textContent = `$${currentRemaining.toFixed(2)}`;
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  private getProgressBarColor(percentage: number): string {
    if (percentage >= 95) return '#dc3545';
    if (percentage >= 70) return '#ffc107';
    return '#28a745';
  }

  private darkenColor(color: string, amount: number): string {
    const hex = color.replace('#', '');
    const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - amount);
    const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - amount);
    const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - amount);
    return `#${r.toString(16).padStart(2, '0')}${g
      .toString(16)
      .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  private animateProgressBar(
    fromPercentage: number,
    toPercentage: number
  ): void {
    const progressBar = document.getElementById('swal-progress-bar');
    const progressText = document.getElementById('swal-progress-text');

    if (!progressBar || !progressText) return;

    setTimeout(() => {
      // Animar la barra
      progressBar.style.width = `${Math.max(toPercentage, 5)}%`;
      progressBar.style.background = `linear-gradient(90deg, ${this.getProgressBarColor(
        toPercentage
      )}, ${this.darkenColor(this.getProgressBarColor(toPercentage), 20)})`;

      // Animar el número
      const duration = 1000;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue =
          fromPercentage + (toPercentage - fromPercentage) * easeOut;

        progressText.textContent = `${Math.round(currentValue)}%`;

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }, 300);
  }

  showWarningAlert(
    title: string,
    text: string,
    confirmButtonText = 'Entendido'
  ) {
    return Swal.fire({
      title,
      text,
      icon: 'warning',
      iconColor: '#f59e0b',
      confirmButtonText,
      confirmButtonColor: '#54BA4A',
    });
  }
}
